import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { neighborhoods, regions, getNeighborhoodsByRegion } from '../data/neighborhoods';
export function NeighborhoodsPage() {
  const [selectedRegion, setSelectedRegion] = useState<string>('All');
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const filteredNeighborhoods = getNeighborhoodsByRegion(selectedRegion);
  const regionColors: Record<string, {
    bg: string;
    text: string;
    border: string;
  }> = {
    Central: {
      bg: 'bg-blue-50',
      text: 'text-blue-900',
      border: 'border-blue-200'
    },
    North: {
      bg: 'bg-purple-50',
      text: 'text-purple-900',
      border: 'border-purple-200'
    },
    South: {
      bg: 'bg-green-50',
      text: 'text-green-900',
      border: 'border-green-200'
    },
    East: {
      bg: 'bg-orange-50',
      text: 'text-orange-900',
      border: 'border-orange-200'
    },
    West: {
      bg: 'bg-rose-50',
      text: 'text-rose-900',
      border: 'border-rose-200'
    }
  };
  return <div className="min-h-screen bg-white" data-id="element-1230">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-1231">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20 md:py-32" data-id="element-1232">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl" data-id="element-1233">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-1234"></div>
            <h1 className="text-7xl md:text-9xl font-light text-gray-900 mb-6 leading-[0.9] tracking-tight" data-id="element-1235">
              Nairobi's
              <br data-id="element-1236" />
              <span className="font-serif italic" data-id="element-1237">Estates</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed font-light max-w-2xl" data-id="element-1238">
              Each district tells its own story of heritage, ambition, and
              refined living.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Region Filter */}
      <div className="sticky top-20 z-40 bg-white border-b border-gray-200" data-id="element-1239">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-6" data-id="element-1240">
          <div className="flex items-center gap-4 overflow-x-auto" data-id="element-1241">
            <span className="text-xs font-light text-gray-500 uppercase tracking-widest flex-shrink-0" data-id="element-1242">
              Region:
            </span>
            {regions.map(region => <button key={region} onClick={() => setSelectedRegion(region)} className={`px-6 py-3 text-sm font-light tracking-wider uppercase whitespace-nowrap transition-all ${selectedRegion === region ? 'bg-gray-900 text-white' : 'border border-gray-200 text-gray-700 hover:border-gray-900'}`} data-id="element-1243">
                {region}
              </button>)}
          </div>
        </div>
      </div>

      {/* Estates Grid - Text Only */}
      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16" data-id="element-1244">
        <div className="mb-8" data-id="element-1245">
          <p className="text-sm text-gray-600 font-light" data-id="element-1246">
            <span className="font-normal text-gray-900" data-id="element-1247">
              {filteredNeighborhoods.length}
            </span>{' '}
            estates
            {selectedRegion !== 'All' && <span data-id="element-1248"> in {selectedRegion}</span>}
          </p>
        </div>

        <div className="space-y-8" data-id="element-1249">
          {filteredNeighborhoods.map((neighborhood, index) => {
          const regionColor = regionColors[neighborhood.region];
          return <motion.div key={neighborhood.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.05
          }} data-id="element-1250">
                <Link to={`/neighborhood/${neighborhood.id}`} className="block group" data-id="element-1251">
                  <div className="p-10 border border-gray-200 hover:border-gray-300 transition-all" data-id="element-1252">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6" data-id="element-1253">
                      <div className="flex-1" data-id="element-1254">
                        <div className="flex items-center gap-3 mb-4" data-id="element-1255">
                          <span className={`px-4 py-2 ${regionColor.bg} ${regionColor.text} text-xs font-light tracking-wider border ${regionColor.border}`} data-id="element-1256">
                            {neighborhood.region}
                          </span>
                        </div>
                        <h3 className="text-4xl font-light text-gray-900 mb-3 group-hover:text-gray-600 transition-colors" data-id="element-1257">
                          {neighborhood.name}
                        </h3>
                        <p className="text-lg font-light text-gray-600 mb-6" data-id="element-1258">
                          {neighborhood.character}
                        </p>
                      </div>
                      <ArrowRight className="w-6 h-6 text-gray-400 group-hover:text-gray-900 group-hover:translate-x-2 transition-all flex-shrink-0 ml-6" strokeWidth={1.5} data-id="element-1259" />
                    </div>

                    {/* Description */}
                    <p className="text-gray-700 leading-relaxed font-light mb-8 text-lg" data-id="element-1260">
                      {neighborhood.description}
                    </p>

                    {/* Best For Tags */}
                    <div className="flex flex-wrap gap-3" data-id="element-1261">
                      {neighborhood.bestFor.map(item => <span key={item} className="px-4 py-2 border border-gray-200 text-gray-700 text-xs font-light tracking-wider" data-id="element-1262">
                          {item}
                        </span>)}
                    </div>
                  </div>
                </Link>
              </motion.div>;
        })}
        </div>
      </div>

      {/* Regional Overview */}
      <div className="bg-gray-50 border-t border-gray-200" data-id="element-1263">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20" data-id="element-1264">
          <div className="h-px w-16 bg-amber-500 mb-8 mx-auto" data-id="element-1265"></div>
          <h2 className="text-5xl font-light text-gray-900 mb-12 text-center tracking-tight" data-id="element-1266">
            By Region
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6" data-id="element-1267">
            {['Central', 'North', 'South', 'East', 'West'].map(region => {
            const count = neighborhoods.filter(n => n.region === region).length;
            const color = regionColors[region];
            return <button key={region} onClick={() => setSelectedRegion(region)} className={`p-8 border-2 ${color.border} ${color.bg} hover:shadow-lg transition-all text-center group`} data-id="element-1268">
                  <h3 className="text-3xl font-light text-gray-900 mb-3" data-id="element-1269">
                    {region}
                  </h3>
                  <p className={`text-sm font-light ${color.text} mb-4`} data-id="element-1270">
                    {count} {count === 1 ? 'estate' : 'estates'}
                  </p>
                  <div className="text-xs text-gray-600 group-hover:text-gray-900 transition-colors uppercase tracking-widest" data-id="element-1271">
                    Explore →
                  </div>
                </button>;
          })}
          </div>
        </div>
      </div>

      <ScrollToTop data-id="element-1272" />
      <Footer data-id="element-1273" />
    </div>;
}